import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DatePicker, Input, Popconfirm, Table, Tag, Tooltip } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useDispatch } from "react-redux";
import { Button, Icon } from "../../../../components/Component";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../../utils/skeleton-templates/table";
import {
  AddAccess,
  EditAccess,
} from "../../../../components/accessControl/AccessControl";
import { deploymentNormApis } from "../../../../api/MachineryEquipment";
import { setExportStatus } from "../../../../store/slices/misc/highlightSlice";
import DeploymentNormEditor from "./DeploymentNormEditor";
import DeploymentNormHistoryDrawer from "./DeploymentNormHistoryDrawer";
import DeploymentNormUploadModal from "./DeploymentNormUploadModal";
import DeploymentNormsExportModal from "./DeploymentNormsExportModal";
import DuplicateDeploymentNormsModal from "./DuplicateDeploymentNormsModal";

/**
 * Machinery Deployment Norms — the per-BoQ-code equipment matrices, VERSIONED.
 *
 * Each row is a Norm Code (B3.1.1…) owning a whole MATRIX of equipment lines.
 * The row shows the version in force at the chosen "as of" instant: when it
 * took effect, how many machines it considers and how many it actually
 * deploys (Prod. Norm > 0). Move the as-of date and the table shows what every
 * norm said on that date — same effective-dating model as Equipment Rates.
 *
 * Rendered inside MachineryNormList's tab shell, which owns the scope selector
 * (projectId 0 = organization master) — hence `projectId` as a prop. Unlike
 * the org-wide fleet, deployment norms ARE scoped: the list re-fetches when
 * the shell's scope changes.
 */

const DeploymentNormList = ({
  projectId = 0,
  projectName = "",
  screenSize = 0,
}: {
  projectId?: number;
  /** Display name for the scope, so tags show "Trumpet HEP" not "Project 112". */
  projectName?: string;
  screenSize?: number;
}) => {
  const dispatch = useDispatch();

  const [dataSource, setDataSource] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  /** null = "now" — the matrices currently in force. */
  const [asOf, setAsOf] = useState<Dayjs | null>(null);
  /** Server-side: matches norm code and description. */
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "50"],
    total: 0,
  });

  // In-place editor swap, exactly as EquipmentRateList does — no routes.
  // deploymentNormId null = create a new norm.
  const [editorOpen, setEditorOpen] = useState<{
    status: boolean;
    deploymentNormId: number | null;
  }>({ status: false, deploymentNormId: null });
  const [historyOpen, setHistoryOpen] = useState<{
    status: boolean;
    deploymentNormId: number | null;
    normCode: string;
  }>({ status: false, deploymentNormId: null, normCode: "" });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);

  const fetchData = useCallback(
    async (params: any = {}) => {
      setLoading(true);
      try {
        // Explicit override wins over state — the as-of picker / search box
        // change and the fetch fires before the state settles.
        const at = params.asOf !== undefined ? params.asOf : asOf;
        const search =
          params.searchQuery !== undefined ? params.searchQuery : searchQuery || null;
        const res = await deploymentNormApis.getList({
          page: params.current ?? 1,
          pageSize: params.pageSize ?? pagination.pageSize,
          direction: params.direction ?? "P",
          sortOrder: "DESC",
          pageDiff: params.pageDiff ?? 0,
          paginationId: params.paginationId ?? null,
          searchQuery: search,
          projectId: params.projectId ?? projectId,
          ...(at ? { asOf: at.toISOString() } : {}),
        });
        if (res?.status) {
          const { list, count } = res.data || {};
          setDataSource(list || []);
          setPagination((prev) => ({
            ...prev,
            current: params.current ?? 1,
            ...(params.pageSize ? { pageSize: params.pageSize } : {}),
            total: Number(count) || 0,
          }));
        }
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize, asOf, searchQuery, projectId]
  );

  // Initial load AND scope changes from the shell's ProjectScopeSelect — the
  // component stays mounted across tab/scope switches.
  useEffect(() => {
    fetchData({ current: 1, projectId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const refresh = () => fetchData({ current: pagination.current });

  const handleAsOfChange = (value: Dayjs | null) => {
    setAsOf(value);
    fetchData({ current: 1, asOf: value });
  };

  // Keyset page moves: forward/backward from the visible edge rows, the same
  // contract every keyset list in the app speaks.
  const handleTableChange = (page: any) => {
    if (page.pageSize !== pagination.pageSize) {
      fetchData({ current: 1, pageSize: page.pageSize });
      return;
    }
    if (page.current === pagination.current) {
      fetchData({ current: page.current });
      return;
    }
    const forward = page.current > pagination.current;
    fetchData({
      current: page.current,
      direction: forward ? "F" : "B",
      paginationId: forward
        ? dataSource[dataSource.length - 1]?.paginationId
        : dataSource[0]?.paginationId,
      pageDiff: Math.abs(page.current - pagination.current) - 1,
    });
  };

  const toggleStatus = async (record: any) => {
    const isActive = record?.status === "Active";
    const res = isActive
      ? await deploymentNormApis.deactivate(Number(record.deploymentNormId))
      : await deploymentNormApis.activate(Number(record.deploymentNormId));
    if (res?.status) refresh();
  };

  const openHistory = (record: any) =>
    setHistoryOpen({
      status: true,
      deploymentNormId: Number(record.deploymentNormId),
      normCode: record.normCode || "",
    });

  const columns: any[] = useMemo(
    () => [
      {
        title: "Norm Code",
        dataIndex: "normCode",
        key: "normCode",
        fixed: "left",
        width: 140,
        render: (code: string, record: any) => (
          <a
            href="#edit"
            onClick={(e) => {
              e.preventDefault();
              setEditorOpen({
                status: true,
                deploymentNormId: Number(record.deploymentNormId),
              });
            }}
            style={{ fontWeight: 600, color: "#2557d6" }}
          >
            {code}
          </a>
        ),
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        width: 260,
        ellipsis: true,
        render: (text: string) => text || "—",
      },
      {
        title: "Equipment",
        dataIndex: "equipmentCount",
        key: "equipmentCount",
        width: 110,
        align: "right",
        // proto int32s always arrive (0, never null) — the "no matrix at all"
        // signal is hasVersion, not a missing count.
        render: (v: any, r: any) => (r.hasVersion ? Number(v) : "—"),
      },
      {
        // How much of the matrix actually earns hours: Prod. Norm > 0.
        title: "Deployed",
        key: "deployed",
        width: 110,
        render: (_: any, r: any) =>
          !r.hasVersion ? (
            "—"
          ) : (
            <span className="text-soft" style={{ fontSize: 12.5 }}>
              {Number(r.deployedCount) || 0} of {Number(r.equipmentCount) || 0}
            </span>
          ),
      },
      {
        // The in-force version's identity at the chosen as-of instant.
        title: "Effective From",
        dataIndex: "effectiveFrom",
        key: "effectiveFrom",
        width: 170,
        render: (v: string | null) =>
          v ? (
            dayjs(v).format("DD MMM YYYY HH:mm")
          ) : (
            <span className="text-soft">
              — <span style={{ fontSize: 11.5 }}>No version yet</span>
            </span>
          ),
      },
      {
        title: "Versions",
        dataIndex: "versionCount",
        key: "versionCount",
        width: 100,
        align: "right",
        render: (v: any, record: any) =>
          Number(v) > 0 ? (
            <a
              href="#history"
              onClick={(e) => {
                e.preventDefault();
                openHistory(record);
              }}
            >
              {Number(v)}
            </a>
          ) : (
            "0"
          ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 100,
        render: (status: string) => (
          <Tag color={status === "Active" ? "green" : "red"}>{status}</Tag>
        ),
      },
      {
        title: "Action",
        key: "action",
        width: 210,
        render: (_: any, record: any) => {
          const isActive = record?.status === "Active";
          return (
            <div className="d-flex align-items-center" style={{ gap: 10 }}>
              <a
                href="#edit"
                onClick={(e) => {
                  e.preventDefault();
                  setEditorOpen({
                    status: true,
                    deploymentNormId: Number(record.deploymentNormId),
                  });
                }}
              >
                Edit
              </a>
              <a
                href="#history"
                onClick={(e) => {
                  e.preventDefault();
                  openHistory(record);
                }}
              >
                History
              </a>
              <EditAccess moduleName="Norms">
                <Popconfirm
                  title={`${isActive ? "Deactivate" : "Activate"} Equipment Norm`}
                  description={`Are you sure you want to ${
                    isActive ? "deactivate" : "activate"
                  } "${record?.normCode}"?`}
                  okText="Yes"
                  cancelText="No"
                  onConfirm={() => toggleStatus(record)}
                >
                  <a
                    href="#status"
                    onClick={(e) => e.preventDefault()}
                    style={{ color: isActive ? "#e85347" : "#1ee0ac" }}
                  >
                    {isActive ? "Deactivate" : "Activate"}
                  </a>
                </Popconfirm>
              </EditAccess>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Drill-in editor replaces the list, exactly as EquipmentRateList does.
  if (editorOpen.status) {
    return (
      <DeploymentNormEditor
        deploymentNormId={editorOpen.deploymentNormId}
        projectId={projectId}
        projectName={projectName}
        onClose={() => setEditorOpen({ status: false, deploymentNormId: null })}
        onSaved={() => {
          setEditorOpen({ status: false, deploymentNormId: null });
          refresh();
        }}
      />
    );
  }

  return (
    <div className="card-inner card-inner-lg p-0">
      {/* Toolbar: a proper full-size search bar on the left, tools on the right. */}
      <div
        className="nk-ibx-head p-0 m-0 mt-2 w-100 d-flex justify-content-between align-items-center flex-wrap"
        style={{ minHeight: "40px", border: "0px", gap: 8 }}
      >
        <div className="ms-2" style={{ flex: "1 1 260px", maxWidth: 360 }}>
          <Input.Search
            allowClear
            placeholder="Search norm code or description…"
            onSearch={(value) => {
              setSearchQuery(value);
              fetchData({ current: 1, searchQuery: value || null });
            }}
          />
        </div>
        <ul className="nk-ibx-head-tools g-1 m-0 p-0 d-flex justify-content-end align-items-center">
          <li className="pt-0 pb-0 d-flex align-items-center" style={{ gap: 6 }}>
            <span className="text-soft" style={{ fontSize: 12 }}>
              Norms as of
            </span>
            <Tooltip title="Versions are effective-dated — pick a date to see the matrix each norm deployed then.">
              <DatePicker
                size="small"
                value={asOf}
                onChange={handleAsOfChange}
                showTime={{ format: "HH:mm" }}
                format="DD MMM YYYY HH:mm"
                placeholder="Now"
                style={{ width: 185 }}
              />
            </Tooltip>
          </li>
          <li className="pt-0 pb-0">
            <Button
              size="sm"
              color="light"
              outline
              className="btn-white"
              title="Refresh"
              onClick={refresh}
            >
              <Icon name={"redo"} />
            </Button>
          </li>
          <li className="pt-0 pb-0">
            <Button
              size="sm"
              color="light"
              outline
              className="btn-white"
              onClick={() => {
                dispatch(setExportStatus("pending"));
                setExportOpen(true);
              }}
            >
              <Icon name={"download"} />
              <span>Export</span>
            </Button>
          </li>
          {/* Duplicate is an ORG-MASTER-only affordance: apply-to-task is
              project-scoped, so the master set must be copied into a project
              before it can be applied there. */}
          {projectId === 0 && (
            <li className="pt-0 pb-0">
              <AddAccess moduleName="Norms">
                <Button
                  size="sm"
                  color="light"
                  outline
                  className="btn-white"
                  onClick={() => setDuplicateOpen(true)}
                >
                  <Icon name={"copy"} />
                  <span>Duplicate to Project</span>
                </Button>
              </AddAccess>
            </li>
          )}
          <li className="pt-0 pb-0">
            <AddAccess moduleName="Norms">
              <Button
                size="sm"
                color="light"
                outline
                className="btn-white"
                onClick={() => setUploadOpen(true)}
              >
                <Icon name={"upload"} />
                <span>Upload Norms</span>
              </Button>
            </AddAccess>
          </li>
          <li className="pt-0 pb-0">
            <AddAccess moduleName="Norms">
              <Button
                size="sm"
                color="primary"
                onClick={() =>
                  setEditorOpen({ status: true, deploymentNormId: null })
                }
              >
                <Icon name={"plus"} />
                <span>Add Norm</span>
              </Button>
            </AddAccess>
          </li>
        </ul>
      </div>

      <div className="content clearfix">
        {/* Full width like EquipmentRateList — no sidebar-width arithmetic. */}
        <div style={{ width: "100%", margin: "10px 0", padding: "0 8px" }}>
          <SkeletonTable
            loading={loading}
            columns={columns as SkeletonTableColumnsType[]}
          >
            <Table
              rowKey="deploymentNormId"
              columns={columns}
              dataSource={dataSource}
              pagination={pagination}
              onChange={handleTableChange}
              bordered
              size="middle"
              scroll={{ x: "max-content" }}
            />
          </SkeletonTable>
        </div>
      </div>

      <DeploymentNormHistoryDrawer
        open={historyOpen.status}
        deploymentNormId={historyOpen.deploymentNormId}
        normCode={historyOpen.normCode}
        onClose={() =>
          setHistoryOpen({ status: false, deploymentNormId: null, normCode: "" })
        }
        onChanged={refresh}
      />
      <DeploymentNormUploadModal
        open={uploadOpen}
        projectId={projectId}
        onClose={() => setUploadOpen(false)}
        onSuccess={refresh}
      />
      <DeploymentNormsExportModal
        open={exportOpen}
        projectId={projectId}
        onClose={() => setExportOpen(false)}
      />
      <DuplicateDeploymentNormsModal
        open={duplicateOpen}
        onClose={() => setDuplicateOpen(false)}
        onSuccess={(targetProjectId) => {
          // Only meaningful when this list is already showing the target
          // project (the button lives on the org-master scope, so normally the
          // copies appear once the user switches scope to that project).
          if (targetProjectId === projectId) refresh();
        }}
      />
    </div>
  );
};

export default DeploymentNormList;
